<template>
    <Card v-if="visibleInvitations.length">
        <CardHeader>
            <div class="flex items-center gap-2">
                <Mail class="size-4 text-muted-foreground" />
                <CardTitle class="text-sm font-medium text-primary">
                    Team invitations
                </CardTitle>
            </div>
        </CardHeader>

        <CardContent>
            <p class="text-sm text-muted-foreground mb-4">
                You have been invited to join the following teams.
            </p>

            <div class="space-y-2">
                <div
                    v-for="invitation in visibleInvitations"
                    :key="invitation.id"
                    class="flex items-center justify-between bg-new-neutral-50 px-3 py-2.5 rounded-md"
                >
                    <div class="flex items-center gap-3">
                        <img
                            v-if="invitation.team?.profile_photo_path || invitation.team?.profile_photo_url"
                            :src="invitation.team.profile_photo_path || invitation.team.profile_photo_url"
                            class="size-8 rounded-full object-cover border border-new-neutral-300"
                        />
                        <span class="text-sm font-medium text-foreground">
                            {{ invitation.team?.name }}
                        </span>
                    </div>
                    <div class="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            :disabled="loading"
                            @click="refuse(invitation)"
                        >
                            Refuse
                        </Button>
                        <Button
                            size="sm"
                            :disabled="loading"
                            @click="accept(invitation)"
                        >
                            Accept
                        </Button>
                    </div>
                </div>
            </div>
        </CardContent>
    </Card>
</template>

<script lang="ts" setup>
import { computed, ref, PropType } from 'vue';
import { router } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { toast } from 'vue-sonner';
import { Mail } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import IInvitation from '@/types/Members/IInvitation';

const props = defineProps({
    invitations: {
        type: Array as PropType<IInvitation[]>,
        default: () => [],
    },
});

const loading = ref(false);

// Pending invitations are passed server-side, so the card renders on first
// paint (no async fetch → no layout jump). Keep one (latest) per team.
const visibleInvitations = computed<IInvitation[]>(() => {
    const pending = props.invitations.filter(
        (i) => i.team && !i.accepted_at && !i.refused_at,
    );

    const latestByTeam = new Map<number, IInvitation>();
    for (const invitation of pending) {
        const existing = latestByTeam.get(invitation.team_id);
        if (!existing || new Date(invitation.created_at) > new Date(existing.created_at)) {
            latestByTeam.set(invitation.team_id, invitation);
        }
    }
    return Array.from(latestByTeam.values());
});

const accept = (invitation: IInvitation) => {
    loading.value = true;
    router.get(
        route('team.invitation.accept', { token: invitation.token }),
        {},
        {
            preserveState: false,
            onSuccess: () => {
                toast.success(`Joined ${invitation.team?.name}`);
            },
            onError: () => {
                toast.error('There was an error accepting the invitation.');
            },
            onFinish: () => {
                loading.value = false;
            },
        }
    );
};

const refuse = (invitation: IInvitation) => {
    loading.value = true;
    router.get(
        route('team.invitation.refuse', { token: invitation.token }),
        {},
        {
            preserveState: false,
            onSuccess: () => {
                toast.success('Invitation declined');
            },
            onError: () => {
                toast.error('There was an error declining the invitation.');
            },
            onFinish: () => {
                loading.value = false;
            },
        }
    );
};
</script>
